import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { customGetAPI } from "services/utils/CustomAPI";
import { themes, scrollTop, optionalAvathar } from "services/constants";
import { Loader } from "components/Loader";
import moment from "moment";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { useSelector } from "react-redux";
import { CustomAutoComplete } from "components/CustomComponents";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import DateRangePicker from "react-bootstrap-daterangepicker";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { showToast } from "services/utils/Status";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  approveStickyFooterBar,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

// Header date columns follow the body's horizontal scroll through a
// scroll-driven CSS animation (compositor-side, same frame as the body), so
// header and body columns can never drift apart the way a JS-mirrored
// scrollLeft did. --mn-range is the body's scrollable width, refreshed on
// resize/data change only. Same technique as Reports > Working Hours Report.
const monthlyHeadShift = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(calc(var(--mn-range, 0px) * -1)); }
`;
const SUPPORTS_SCROLL_TIMELINE =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("animation-timeline: scroll()");
const scrollDrivenHeadCellSx = SUPPORTS_SCROLL_TIMELINE
  ? {
      animation: `${monthlyHeadShift} auto linear both`,
      animationTimeline: "--mnx",
    }
  : {};

export const Monthly = ({
  loading,
  isLoading,
  monthlyShiftList,
  listOfDate,
  filterChanges,
  setFilterChanges,
  shiftGrpError,
  setGrpNameError,
  openDialog,
  setOpenDialog,
  updateMonthlyShift,
  departmentIdValue,
  departmentNames,
  masterDataForSearch,
}) => {
  const rosterTableHeaderCellSx = {
    ...designShell.tableHeadCellBase,
    fontWeight: 700,
    backgroundColor: designColors.slate100,
    whiteSpace: "nowrap",
    position: "sticky",
    top: APP_BAR_OFFSET,
    zIndex: 2,
  };
  const rosterTableSx = {
    width: `${190 + listOfDate.length * 136}px`,
    minWidth: "100%",
    tableLayout: "fixed",
  };
  const panelContentSx = {
    px: 3,
    py: 2.5,
  };
  const panelLabelSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
  };

  const [page, setLeavePage] = useState(0);
  const [rowsPerPage, setLeaveRowsPerPage] = useState(25);
  const [userShiftsDet, setUserShiftsDet] = useState([]);
  const [curShiftDetail, setCurShiftDetail] = useState([]);
  const [shiftName, setShiftName] = useState("");
  const [shiftId, setShiftId] = useState("");
  const [shiftData, setShiftData] = useState([]);
  const [triggerApi, setTriggerApi] = useState(false);
  const [getSftDate, setgetSftDate] = useState("");
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // Same value for every shift cell in the table — see Daily.js for the
  // fuller explanation of why this was hoisted and why cells now show a
  // hover tooltip instead of a click-time toast.
  const canEditShift = checkEditorPermission(
    userScreenAccess?.module_details,
    "Rosters",
    "Roster",
  );
  const [disable, setDisable] = useState(true);
  const handleChangeRowsPerPage = (event) => {
    setLeaveRowsPerPage(parseInt(event.target.value, 10));
    setLeavePage(0);
  };
  const containerRef = useRef(null);

  const sortedMonthlyShiftList = monthlyShiftList;

  const dateStr = getSftDate;
  const [day, month, year] = dateStr.split("-");
  const formattedDate = `${year}-${month}-${day}`;
  const [latestPayDate, setLatestPayDate] = useState("");
  const [selectedDates, setSelectedDates] = useState([]);
  const [state, setState] = useState(() => {
    const formattedLatestPayDate = moment(latestPayDate, "DD-MM-YYYY").format(
      "YYYY-MM-DD",
    );
    const index = listOfDate.indexOf(formattedLatestPayDate);

    const startDate =
      index !== -1 && index + 1 < listOfDate.length
        ? moment(listOfDate[index + 1])
        : moment(listOfDate[0]);

    const endDate = moment(listOfDate[listOfDate.length - 1]);

    return {
      start: startDate,
      end: endDate,
    };
  });
  const headerContainerRef = useRef(null);
  // Sticky "proxy" horizontal scrollbar pinned to the bottom of the screen
  // (above the footer bar when it's shown) - the body's own bar is hidden
  // because it only appears at the very end of a long roster.
  const proxyScrollRef = useRef(null);
  const proxyInnerRef = useRef(null);
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(56);
  const hasFooter = (monthlyShiftList?.length || 0) > 25;

  useEffect(() => {
    const body = containerRef.current;
    const head = headerContainerRef.current;
    const proxy = proxyScrollRef.current;
    const proxyInner = proxyInnerRef.current;
    if (!body || !head || !proxy || !proxyInner) return undefined;
    const publish = () => {
      head.style.setProperty(
        "--mn-range",
        `${Math.max(0, body.scrollWidth - body.clientWidth)}px`,
      );
      proxyInner.style.width = `${body.scrollWidth}px`;
      proxy.style.display =
        body.scrollWidth > body.clientWidth + 1 ? "block" : "none";
      if (footerRef.current) {
        setFooterHeight(footerRef.current.offsetHeight || 56);
      }
    };
    publish();
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(publish);
      observer.observe(body);
      if (body.firstElementChild) observer.observe(body.firstElementChild);
      // Re-measure whenever the footer bar itself changes height.
      if (footerRef.current) observer.observe(footerRef.current);
    }
    window.addEventListener("resize", publish);
    // One side DRIVES at a time: scroll events arrive a frame or more after
    // the scroll that caused them, so the other side's echo events carry OLD
    // positions and writing them back snapped the scroll to where it had
    // been. The side that scrolls first drives; the other is ignored until
    // the driver has been idle for a moment.
    let driver = null;
    let idleTimer = null;
    const drive = (who, from, to) => {
      if (driver && driver !== who) return;
      driver = who;
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        driver = null;
      }, 150);
      if (Math.abs(to.scrollLeft - from.scrollLeft) >= 1) {
        to.scrollLeft = from.scrollLeft;
      }
      // Browsers without scroll-driven animation: mirror the header here.
      if (!SUPPORTS_SCROLL_TIMELINE) head.scrollLeft = body.scrollLeft;
    };
    const onBodyScroll = () => drive("body", body, proxy);
    const onProxyScroll = () => drive("proxy", proxy, body);
    body.addEventListener("scroll", onBodyScroll, { passive: true });
    proxy.addEventListener("scroll", onProxyScroll, { passive: true });
    return () => {
      window.clearTimeout(idleTimer);
      window.removeEventListener("resize", publish);
      if (observer) observer.disconnect();
      body.removeEventListener("scroll", onBodyScroll);
      proxy.removeEventListener("scroll", onProxyScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, listOfDate?.length, monthlyShiftList?.length, page, rowsPerPage]);

  useEffect(() => {
    if (formattedDate && listOfDate.includes(formattedDate)) {
      setState({
        start: moment(formattedDate),
        end: moment(formattedDate),
      });
      setSelectedDates([formattedDate]);
      return;
    }

    const formattedLatestPayDate = moment(latestPayDate, "DD-MM-YYYY").format(
      "YYYY-MM-DD",
    );
    const index = listOfDate.indexOf(formattedLatestPayDate);

    const startDate =
      index !== -1 && index + 1 < listOfDate.length
        ? moment(listOfDate[index + 1])
        : moment(listOfDate[0]);

    const endDate = moment(listOfDate[listOfDate.length - 1]);
    setState({
      start: startDate,
      end: endDate,
    });
    if (index !== -1 && index + 1 < listOfDate.length) {
      setSelectedDates(listOfDate.slice(index + 1));
    } else {
      setSelectedDates(listOfDate);
    }
  }, [latestPayDate, listOfDate, formattedDate]);

  useEffect(() => {
    if (filterChanges) {
      setFilterChanges(false);
      setLeavePage(0);
    }
  }, [filterChanges, setFilterChanges]);

  const { start, end } = state;
  const handleCallback = (start, end) => {
    setState({ start, end });

    const dates = [];
    const currentDate = moment(start);

    while (currentDate.isSameOrBefore(end, "day")) {
      dates.push(currentDate.format("YYYY-MM-DD"));
      currentDate.add(1, "day");
    }
    setSelectedDates(dates);
    setShiftName("");
    setDisable(false);
  };

  const label = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");

  const handleChangeShift = (event, data) => {
    if (data) {
      setShiftName(data?.label);
      setShiftId(data?.shift_id);
      setDisable(false);
    } else {
      setShiftName("");
      setShiftId("");
    }
  };

  const hasDatesBeforeLatestPayDate = selectedDates.some((date) =>
    moment(date).isBefore(moment(latestPayDate, "DD-MM-YYYY"), "day"),
  );

  const updateIndividualShift = async () => {
    isLoading(true);
    const shift_assignments = selectedDates.map((date) => ({
      shift_id: shiftId ?? curShiftDetail?.shift_id,
      date: date,
      is_week_off: shiftName === "Weekoff",
      unassigned: curShiftDetail?.shift_name === "Unassigned",
    }));
    if (shiftName !== "" && !hasDatesBeforeLatestPayDate) {
      updateMonthlyShift(userShiftsDet?.user_id, shift_assignments);
      setOpenDialog(false);
      const startDate = moment(listOfDate[0]);
      const endDate = moment(listOfDate[listOfDate.length - 1]);
      setState({ start: startDate, end: endDate });
      setSelectedDates(listOfDate);
    } else if (shiftName === "") {
      isLoading(false);
      setGrpNameError(true);
    } else {
      isLoading(false);
      showToast("error", "Selected detes must after previous payrun date");
    }
  };

  useEffect(() => {
    const fetchIndividualUserDetails = async () => {
      const res = await customGetAPI(`${domain}all/shift-filter/`);
      if (res.ok) {
        setShiftData(res?.responseDetails);
      }
    };
    if (triggerApi) {
      fetchIndividualUserDetails();
      setTriggerApi(false);
    }
  }, [domain, triggerApi]);

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const clearValues = () => {
    setOpenDialog(false);
    setDisable(true);
    setGrpNameError(false);
    const startDate = moment(listOfDate[0]);
    const endDate = moment(listOfDate[listOfDate.length - 1]);
    setState({ start: startDate, end: endDate });
    setSelectedDates(listOfDate);
  };

  const getShiftCellSx = (shift) => {
    const shiftName = (shift?.shift_name || "").toLowerCase();

    if (
      !shift?.shift_name ||
      shiftName === "unassigned" ||
      shiftName === "not joined"
    ) {
      return {
        backgroundColor: "#F8FAFC",
        color: "#475569",
      };
    }

    if (shiftName === "weekoff") {
      return {
        backgroundColor: "#F8FAFC",
        color: "#334155",
      };
    }

    if (shiftName === "holiday") {
      return {
        backgroundColor: "#EFF8FF",
        color: "#075985",
      };
    }

    if (shiftName === "leave") {
      return {
        backgroundColor: "#FFF1F2",
        color: "#E11D48",
      };
    }

    return {
      backgroundColor: shift?.color_code || "#EEF4FF",
      color: "#14224F",
    };
  };

  const getShiftDotColor = (shift) => {
    const shiftName = (shift?.shift_name || "").toLowerCase();

    if (
      !shift?.shift_name ||
      shiftName === "unassigned" ||
      shiftName === "not joined"
    ) {
      return "#94A3B8";
    }
    if (shiftName === "weekoff") {
      return "#9CA3AF";
    }
    if (shiftName === "holiday") {
      return "#0EA5E9";
    }
    if (shiftName === "leave") {
      return "#F43F5E";
    }

    return shift?.color_code?.includes("#") ? "#7C3AED" : "#16A34A";
  };

  const panelFooter = (
    <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end">
      <SecondaryButton onClick={clearValues}>Cancel</SecondaryButton>
      <PrimaryButton onClick={updateIndividualShift} disabled={disable}>
        Update
      </PrimaryButton>
    </Mui.Stack>
  );

  return (
    <React.Fragment>
      {loading ? (
        <Mui.Box>
          <Loader info="Loading..." />
        </Mui.Box>
      ) : (
        <Mui.Box sx={SUPPORTS_SCROLL_TIMELINE ? { timelineScope: "--mnx" } : undefined}>
          <Mui.Box
            sx={{
              // Column-header strip is meaningless once rows become cards.
              display: { xs: "none", md: "block" },
              mt: 2,
              position: "sticky",
              top: APP_BAR_OFFSET,
              zIndex: 3,
            }}
          >
            <Mui.Box
              sx={{
                width: "100%",
                border: `1px solid ${designColors.slate200}`,
                borderRadius: 0,
                boxShadow: "none",
                backgroundColor: designColors.white,
                overflow: "hidden",
              }}
            >
              <Mui.Box
                ref={headerContainerRef}
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  overflowY: "hidden",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                <Mui.Table sx={rosterTableSx}>
                  <colgroup>
                    <col style={{ width: 190 }} />
                    {listOfDate?.map((value) => (
                      <col key={value} style={{ width: 136 }} />
                    ))}
                  </colgroup>
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow>
                      <Mui.TableCell
                        sx={{
                          ...rosterTableHeaderCellSx,
                          minWidth: 190,
                          width: 190,
                          zIndex: 4,
                          position: "sticky",
                          left: 0,
                        }}
                      >
                        Employee Name
                      </Mui.TableCell>
                      {listOfDate?.map((value) => (
                        <Mui.TableCell
                          key={value}
                          sx={{
                            ...rosterTableHeaderCellSx,
                            ...scrollDrivenHeadCellSx,
                            minWidth: 136,
                            width: 136,
                            textAlign: "center",
                          }}
                        >
                          <Mui.Stack
                            direction="column"
                            sx={{
                              alignItems: "center",
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                color: "#14224F",
                                fontWeight: "bold",
                                fontSize: 18,
                                lineHeight: 1.1,
                              }}
                            >
                              {moment(value, "YYYY-MM-DD").format("DD")}
                            </Mui.Typography>
                            <Mui.Typography
                              sx={{
                                color: "#475569",
                                fontWeight: "bold",
                                fontSize: 12,
                              }}
                            >
                              {moment(value, "YYYY-MM-DD").format("ddd")}
                            </Mui.Typography>
                          </Mui.Stack>
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                </Mui.Table>
              </Mui.Box>
            </Mui.Box>
          </Mui.Box>

          <Mui.Grid container>
            <Mui.Box
              ref={containerRef}
              sx={{
                mt: 0.5,
                width: "100%",
                // Cards on phones/tablets need no sideways scroll.
                overflowX: { xs: "visible", md: "auto" },
                overflowY: "visible",
                overscrollBehaviorX: "contain",
                // Native bar hidden: the sticky proxy bar below replaces it.
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                ...(SUPPORTS_SCROLL_TIMELINE
                  ? { scrollTimeline: "--mnx x" }
                  : {}),
              }}
            >
              <Mui.TableContainer
                component={Mui.Paper}
                sx={{
                  width: { xs: "100%", md: "max-content" },
                  minWidth: "100%",
                  border: `1px solid ${designColors.slate200}`,
                  borderRadius: 0,
                  boxShadow: "none",
                  backgroundColor: designColors.white,
                  overflow: "visible",
                }}
              >
                <ApproveTable sx={rosterTableSx}>
                  <colgroup>
                    <col style={{ width: 190 }} />
                    {listOfDate?.map((value) => (
                      <col key={value} style={{ width: 136 }} />
                    ))}
                  </colgroup>
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow>
                      <Mui.TableCell
                        sx={{
                          ...rosterTableHeaderCellSx,
                          minWidth: 190,
                          width: 190,
                          borderBottom: "none",
                          py: 0,
                          height: 0,
                          lineHeight: 0,
                        }}
                      >
                        <Mui.Box
                          sx={{
                            visibility: "hidden",
                            height: 0,
                            overflow: "hidden",
                          }}
                        >
                          Employee Name
                        </Mui.Box>
                      </Mui.TableCell>
                      {listOfDate?.map((value) => (
                        <Mui.TableCell
                          key={value}
                          sx={{
                            ...rosterTableHeaderCellSx,
                            minWidth: 136,
                            width: 136,
                            textAlign: "center",
                            borderBottom: "none",
                            py: 0,
                            height: 0,
                            lineHeight: 0,
                          }}
                        >
                          <Mui.Box
                            sx={{
                              visibility: "hidden",
                              height: 0,
                              overflow: "hidden",
                            }}
                          >
                            {moment(value, "YYYY-MM-DD").format("DD")}
                          </Mui.Box>
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {sortedMonthlyShiftList?.length === 0 ? (
                      <TableEmptyState dataLength={masterDataForSearch} />
                    ) : (
                      sortedMonthlyShiftList
                        ?.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        ?.map((shiftList, index) => (
                          <React.Fragment
                            key={
                              shiftList?.user_id ||
                              `${shiftList?.user_name}-${index}`
                            }
                          >
                            <Mui.TableRow
                              key={
                                shiftList?.user_id ||
                                `${shiftList?.user_name}-${index}`
                              }
                              sx={{
                                cursor: "pointer",
                                "& td": {
                                  borderBottom: `1px solid ${designColors.slate200}`,
                                },
                              }}
                            >
                              <Mui.TableCell
                                data-label="Employee"
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  backgroundColor: designColors.white,
                                  zIndex: 1,
                                  minWidth: 190,
                                  width: 190,
                                  boxShadow: "1px 0 0 rgba(226, 232, 240, 0.9)",
                                }}
                              >
                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Mui.Avatar
                                    sx={{ width: 30, height: 30 }}
                                    alt={shiftList?.user_name
                                      ?.charAt(0)
                                      .toUpperCase()}
                                    src={
                                      shiftList?.image_url ?? optionalAvathar
                                    }
                                  />
                                  {shiftList.user_name?.length > 10 ? (
                                    <Mui.Tooltip
                                      title={shiftList.user_name}
                                      arrow
                                      slotProps={tooltipStyleProps}
                                    >
                                      <Mui.Typography
                                        sx={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          maxWidth: 132,
                                          fontWeight: 600,
                                          color: "#14224F",
                                        }}
                                      >
                                        {shiftList.user_name}
                                      </Mui.Typography>
                                    </Mui.Tooltip>
                                  ) : (
                                    <Mui.Typography
                                      sx={{ fontWeight: 600, color: "#14224F" }}
                                    >
                                      {shiftList.user_name}
                                    </Mui.Typography>
                                  )}
                                </Mui.Stack>
                              </Mui.TableCell>
                              {/* Was `shiftList?.shifts.map(...)` -- purely
                                  positional, so it silently renders the
                                  wrong day's shift under a date column
                                  whenever this row's `shifts` array isn't
                                  perfectly 1:1, in-order with `listOfDate`
                                  (the export's own buildRosterExportRow
                                  already aligns by `shift.date` instead,
                                  which is why the two could show different
                                  times for what looks like the same date).
                                  Align on-screen the same way, falling back
                                  to position only when no date match
                                  exists. */}
                              {listOfDate
                                .map((dateStr) => {
                                  const match = (shiftList?.shifts || []).find(
                                    (candidate) => {
                                      const normalized = moment(
                                        candidate?.date,
                                        ["DD-MM-YYYY", "YYYY-MM-DD"],
                                        true,
                                      );
                                      return (
                                        normalized.isValid() &&
                                        normalized.format("YYYY-MM-DD") ===
                                          dateStr
                                      );
                                    },
                                  );
                                  return {
                                    columnDate: dateStr,
                                    shift:
                                      match ||
                                      shiftList?.shifts?.[
                                        listOfDate.indexOf(dateStr)
                                      ],
                                  };
                                })
                                .map(({ columnDate, shift }) => (
                                  // Keyed by the COLUMN date (always unique),
                                  // not shift.date: the positional fallback
                                  // can hand two columns the same shift, and
                                  // duplicate keys made React leave stale
                                  // extra cells squeezed past the last column.
                                  <Mui.Tooltip
                                    key={`${shiftList?.user_id || shiftList?.user_name || index}-${columnDate}`}
                                    title={
                                      canEditShift
                                        ? ""
                                        : "Access denied: You are not authorized to change shift"
                                    }
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <Mui.TableCell
                                      data-label={moment(columnDate, "YYYY-MM-DD").format("ddd, DD MMM")}
                                      sx={{
                                        p: 0.75,
                                        backgroundColor: designColors.white,
                                        textAlign: "center",
                                        minWidth: 136,
                                        width: 136,
                                        cursor: canEditShift
                                          ? "pointer"
                                          : "default",
                                      }}
                                      onClick={() => {
                                        if (!canEditShift) return;
                                        setgetSftDate(shift?.date);
                                        setUserShiftsDet(shiftList);
                                        setCurShiftDetail(shift);
                                        setLatestPayDate(
                                          shiftList?.latest_pay_date,
                                        );
                                        setShiftName(
                                          shift?.shift_name === "Unassigned"
                                            ? ""
                                            : shift?.shift_name,
                                        );
                                        setShiftId(shift?.shift_id);
                                        setOpenDialog(true);
                                        setTriggerApi(true);
                                        if (shift?.date) {
                                          const [
                                            clickedDay,
                                            clickedMonth,
                                            clickedYear,
                                          ] = shift.date.split("-");
                                          const clickedDate = `${clickedYear}-${clickedMonth}-${clickedDay}`;
                                          setState({
                                            start: moment(clickedDate),
                                            end: moment(clickedDate),
                                          });
                                          setSelectedDates([clickedDate]);
                                        }
                                      }}
                                    >
                                      <Mui.Box
                                        sx={{
                                          ...getShiftCellSx(shift),
                                          minHeight: 62,
                                          borderRadius: 2,
                                          px: 1,
                                          py: 0.9,
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Mui.Stack
                                          direction="column"
                                          width={"100%"}
                                        >
                                          <Mui.Stack
                                            direction="row"
                                            spacing={0.6}
                                            alignItems="center"
                                            justifyContent="center"
                                          >
                                            <Mui.Box
                                              sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: "50%",
                                                backgroundColor:
                                                  getShiftDotColor(shift),
                                                flexShrink: 0,
                                              }}
                                            />
                                            <Mui.Typography
                                              sx={{
                                                fontSize: 12,
                                                fontWeight: "bold",
                                              }}
                                            >
                                              {shift?.shift_name
                                                ? shift?.shift_name
                                                : "Not Joined"}
                                            </Mui.Typography>
                                          </Mui.Stack>
                                          {shift?.shift_name === null ||
                                          shift?.shift_name === "Weekoff" ? (
                                            ""
                                          ) : shift?.shift_start_time &&
                                            shift?.shift_end_time ? (
                                            <>
                                              <Mui.Typography
                                                sx={{ fontSize: 10 }}
                                              >
                                                {moment(
                                                  shift?.shift_start_time,
                                                  "HH:mm:SS",
                                                ).format("h:mm a")}
                                                {" to "}{" "}
                                                {moment(
                                                  shift?.shift_end_time,
                                                  "HH:mm:SS",
                                                ).format("h:mm a")}
                                              </Mui.Typography>
                                            </>
                                          ) : null}
                                        </Mui.Stack>
                                      </Mui.Box>
                                    </Mui.TableCell>
                                  </Mui.Tooltip>
                                ))}
                            </Mui.TableRow>
                          </React.Fragment>
                        ))
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
            </Mui.Box>
            <Mui.Box
              ref={proxyScrollRef}
              sx={{
                position: "sticky",
                // The footer bar sticks at bottom:-1, so overlap by that 1px to leave no gap.
                bottom: hasFooter ? Math.max(0, footerHeight - 1) : 0,
                zIndex: 4,
                overflowX: "auto",
                overflowY: "hidden",
                width: "100%",
                display: "none",
                backgroundColor: designColors.white,
              }}
            >
              <Mui.Box ref={proxyInnerRef} sx={{ height: "1px" }} />
            </Mui.Box>
          </Mui.Grid>
          {sortedMonthlyShiftList.length > 25 && (
            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: sortedMonthlyShiftList.length,
                itemLabel: "employees",
                onPageChange: setLeavePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          )}
          {openDialog ? (
            <GlobalSidePanel
              onClose={clearValues}
              open={openDialog}
              title="Edit Shift Details"
              subtitle="Review and update the selected employee shift assignment."
              bodySx={panelContentSx}
              footer={panelFooter}
            >
              <Mui.Stack spacing={2.75}>
                <RequestDetailHeader
                  userDetails={{
                    image_url: userShiftsDet?.image_url,
                    employee_id: userShiftsDet?.employee_id,
                    user_name: userShiftsDet?.user_name,
                    department_name:
                      userShiftsDet?.department_name || departmentNames,
                    designation: userShiftsDet?.designation,
                  }}
                />
                <Mui.Box>
                  <GlobalFieldLabel helperText="Define a full month range to update shifts throughout the selected month.">
                    <Mui.Box component="span" sx={panelLabelSx}>
                      Date
                    </Mui.Box>
                  </GlobalFieldLabel>
                  <DateRangePicker
                    initialSettings={{
                      startDate: start.toDate(),
                      endDate: end.toDate(),
                      applyClass: "custom-apply-btn",
                      cancelClass: "custom-cancel-btn",
                      minDate: latestPayDate
                        ? moment(latestPayDate, "DD-MM-YYYY")
                            .add(1, "day")
                            .toDate()
                        : false,
                      locale: {
                        format: "DD/MM/YYYY",
                      },
                      // isInvalidDate: (date) => {
                      //   const formatted = date.format("YYYY-MM-DD");
                      //   return !selectedDates.includes(formatted);
                      // },
                    }}
                    onCallback={handleCallback}
                  >
                    <Mui.TextField
                      id="reportrange"
                      variant="outlined"
                      label=""
                      placeholder="Select a date range"
                      value={label}
                      size="small"
                      InputProps={{
                        readOnly: true,
                        notched: false,
                        endAdornment: (
                          <Mui.InputAdornment position="end">
                            <Mui.IconButton>
                              <DateRangeIcon />
                            </Mui.IconButton>
                          </Mui.InputAdornment>
                        ),
                      }}
                      InputLabelProps={{ shrink: true, required: true }}
                      fullWidth
                      sx={{
                        ...globalTextFieldSx,
                        "& .MuiOutlinedInput-notchedOutline": {
                          legend: {
                            display: "none",
                          },
                        },
                      }}
                    />
                  </DateRangePicker>
                </Mui.Box>
                <Mui.Box>
                  <GlobalFieldLabel helperText="Select the appropriate shift to assign for the chosen date. Shows all available shifts.">
                    <Mui.Box component="span" sx={panelLabelSx}>
                      Shift Name
                    </Mui.Box>
                  </GlobalFieldLabel>
                  <CustomAutoComplete
                    dataList={shiftData
                      ?.filter((obj) => obj?.shift_name !== shiftName)
                      .map((obj) => ({
                        ...obj,
                        label: obj?.shift_name,
                      }))}
                    selectedValue={shiftName}
                    updateDataFunction={handleChangeShift}
                    placeHolder="Select shift group name"
                  />
                  {shiftGrpError ? (
                    <Mui.Typography
                      sx={{
                        pt: 0.5,
                        color: themes.helperTextColor,
                        fontSize: 12,
                      }}
                    >
                      Shift name is required
                    </Mui.Typography>
                  ) : null}
                </Mui.Box>
              </Mui.Stack>
            </GlobalSidePanel>
          ) : null}
        </Mui.Box>
      )}
    </React.Fragment>
  );
};
